import { deleteCookie, getCookie, getWindowScopedCookieKey, setCookie } from './cookies';

const STORAGE_PREFIX = 'dt_currentShopId';

/** Sentinel stored in session when the user views all accessible shops at once. */
export const ALL_SHOPS_ID = '__ALL_SHOPS__';

export const CURRENT_SHOP_HEADER = 'X-Current-Shop-Id';
export const CURRENT_SHOP_CHANGED_EVENT = 'dt:current-shop-changed';

export function isAllShopsSelection(shopId: string | null | undefined): boolean {
  return shopId === ALL_SHOPS_ID;
}

export function getCurrentShopStorageKey(userId: string): string {
  return `${STORAGE_PREFIX}_${userId}`;
}

/** Read persisted shop id for this browser tab (sessionStorage), with legacy fallbacks. */
export function readStoredCurrentShopId(userId: string | undefined): string | null {
  if (!userId || typeof window === 'undefined') return null;

  const fromSession = sessionStorage.getItem(getCurrentShopStorageKey(userId));
  if (fromSession) return fromSession;

  const fromLocal = localStorage.getItem(getCurrentShopStorageKey(userId));
  if (fromLocal) return fromLocal;

  const legacyKey = getWindowScopedCookieKey('currentShopId');
  return getCookie(legacyKey);
}

/** Persist shop id for this user in the current browser tab/window. */
export function writeStoredCurrentShopId(userId: string, shopId: string): void {
  if (typeof window === 'undefined') return;

  sessionStorage.setItem(getCurrentShopStorageKey(userId), shopId);

  const legacyKey = getWindowScopedCookieKey('currentShopId');
  setCookie(legacyKey, shopId);

  window.dispatchEvent(
    new CustomEvent(CURRENT_SHOP_CHANGED_EVENT, { detail: { shopId, userId } }),
  );
}

export function clearStoredCurrentShopId(userId: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(getCurrentShopStorageKey(userId));
  localStorage.removeItem(getCurrentShopStorageKey(userId));
  deleteCookie(getWindowScopedCookieKey('currentShopId'));
}

/**
 * Identity used to scope the stored shop selection.
 * `entityId` (admin / practitioner / employee id) is the id the backoffice holds
 * in its auth state, so reader and writer must both resolve to it.
 */
export function readUserIdFromToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (
      (payload?.entityId as string | undefined) ??
      (payload?.userId as string | undefined) ??
      null
    );
  } catch {
    return null;
  }
}

export function readCurrentShopIdFromToken(): string | null {
  const userId = readUserIdFromToken();
  if (!userId) return null;
  const stored = readStoredCurrentShopId(userId);
  if (isAllShopsSelection(stored)) return null;
  return stored;
}

export function isAllShopsModeFromToken(): boolean {
  const userId = readUserIdFromToken();
  if (!userId) return false;
  return isAllShopsSelection(readStoredCurrentShopId(userId));
}

/** Append allShops=true to GET list endpoints when viewing all accessible shops. */
export function withShopScopeQuery(endpoint: string, method = 'GET'): string {
  if (method !== 'GET' && method !== 'HEAD') return endpoint;
  if (!isAllShopsModeFromToken()) return endpoint;
  if (endpoint.includes('allShops=true')) return endpoint;
  const sep = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${sep}allShops=true`;
}
